import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
  Stack
} from "@fluentui/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ContractItem } from "../../contract.interface";
import { phoneNumberFormat } from "../../ProductColumns/helpers";

import { LineAdditionalServiceForm } from "./components/LineAdditionalServiceForm";
import { LineAdditionalServicesList } from "./components/LineAdditionalServicesList/LineAdditionalServicesList";
import { PhysicalChipDeliveryForm } from "./components/PhysicalChipDeliveryForm";
import {
  dataBonusAllowedCarriers,
  HAND_DELIVERY_TRACKING_CODE,
  isLineAdditionalServiceStepTerminal,
  LINE_ADDITIONAL_SERVICES_QUERY_TOKEN,
  paymentLinkPollingIntervalInMilliseconds,
  paymentLinkPollingMaximumAttempts
} from "./constants";
import {
  ProcessLineAdditionalServiceStepPayload,
  CreateLineAdditionalServicePayload,
  LineAdditionalService,
  LineAdditionalServiceCategory,
  LineAdditionalServiceFormValues,
  LineAdditionalServiceInteractionAction,
  LineAdditionalServiceListItem,
  LineAdditionalServiceType,
  PhysicalChipDeliveryFormValues
} from "./interfaces";

import { PaymentMethodType } from "@/common/types/PaymentMethod.types";
import { PaymentRequestTypeProviders } from "@/common/types/PaymentRequestTypes";
import { useGet } from "@/core/libs/api/react-query/hook/use-get";
import { useApi } from "@/core/libs/api/react-query/useApi";
import { useConfirmModal } from "@/hooks/useConfirmModal";

interface LineAdditionalServicesModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  contractId: number;
  item?: ContractItem;
}

const lineAdditionalServiceFormId = "line-additional-service-form";
const physicalChipDeliveryFormId = "physical-chip-delivery-form";

type ModalView =
  | { mode: "list" }
  | { mode: "create" }
  | {
      mode: "confirmPhysicalChipDelivery";
      service: LineAdditionalServiceListItem;
    };

function getSuccessMessage({
  value,
  paymentMethod
}: LineAdditionalServiceFormValues): string {
  if (value === 0) return "Serviço registrado sem cobrança.";

  switch (paymentMethod) {
    case PaymentMethodType.PIX:
      return "Serviço registrado. Envie o link do portal Coopercompany ao cooperado.";

    case PaymentMethodType.INVOICE_DEBIT:
      return "Serviço registrado com cobrança em fatura.";
  }
}

export function LineAdditionalServicesModal({
  isOpen,
  onDismiss,
  item,
  contractId
}: LineAdditionalServicesModalProps): JSX.Element {
  const { execute } = useApi();
  const { openModalWithSettings } = useConfirmModal();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalView, setModalView] = useState<ModalView>({ mode: "list" });
  const [processingServiceId, setProcessingServiceId] = useState<number>();
  const [hasPaymentLinkPollingTimedOut, setHasPaymentLinkPollingTimedOut] =
    useState(false);
  const paymentLinkPollingAttempts = useRef(0);

  const { data: servicesResponse, isFetching: isLoading } = useGet<{
    data: Array<LineAdditionalServiceListItem>;
  }>({
    url: "line-additional-services",
    queryToken: LINE_ADDITIONAL_SERVICES_QUERY_TOKEN,
    params: { lineId: item?.lineId, contractId },
    enabled: isOpen && Boolean(item?.lineId),
    errorMessage: "Não foi possível carregar os serviços adicionais da linha."
  });

  useEffect(() => {
    setModalView({ mode: "list" });
  }, [contractId, isOpen, item?.lineId]);

  const services = servicesResponse?.data ?? [];
  const isCreatingService = modalView.mode === "create";
  const isShowingForm = modalView.mode !== "list";
  const isBusy = isSubmitting || processingServiceId !== undefined;
  const isWaitingForPaymentLink = services.some(
    service =>
      !isLineAdditionalServiceStepTerminal(service) &&
      service.nextStep?.interactionAction ===
        LineAdditionalServiceInteractionAction.CONFIRM_PAYMENT &&
      service.paymentRequest?.typeProvider ===
        PaymentRequestTypeProviders.PIX &&
      !service.paymentRequest?.transactionId
  );

  useEffect(() => {
    if (!isOpen || !isWaitingForPaymentLink) {
      paymentLinkPollingAttempts.current = 0;
      setHasPaymentLinkPollingTimedOut(false);

      return;
    }

    if (isShowingForm || hasPaymentLinkPollingTimedOut) return;

    let isCanceled = false;
    let timeoutId: number;

    const pollPaymentLink = async () => {
      await queryClient.refetchQueries({
        queryKey: [LINE_ADDITIONAL_SERVICES_QUERY_TOKEN]
      });

      if (isCanceled) return;

      paymentLinkPollingAttempts.current += 1;

      if (
        paymentLinkPollingAttempts.current >= paymentLinkPollingMaximumAttempts
      ) {
        setHasPaymentLinkPollingTimedOut(true);

        return;
      }

      timeoutId = window.setTimeout(
        () => void pollPaymentLink(),
        paymentLinkPollingIntervalInMilliseconds
      );
    };

    timeoutId = window.setTimeout(
      () => void pollPaymentLink(),
      paymentLinkPollingIntervalInMilliseconds
    );

    return () => {
      isCanceled = true;
      window.clearTimeout(timeoutId);
    };
  }, [
    hasPaymentLinkPollingTimedOut,
    isShowingForm,
    isOpen,
    isWaitingForPaymentLink,
    queryClient
  ]);

  const lineNumber = item?.line?.number
    ? phoneNumberFormat(String(item.line.number))
    : "-";

  const carrierName = item?.offerPrice?.offer?.plan?.carrier?.name
    ?.trim()
    .toUpperCase();

  const allowedServiceCategories = dataBonusAllowedCarriers.includes(
    carrierName
  )
    ? [
        LineAdditionalServiceCategory.ACTIVATION_C,
        LineAdditionalServiceCategory.DATA_BONUS
      ]
    : [LineAdditionalServiceCategory.ACTIVATION_C];

  const handleClose = () => {
    if (isBusy) return;

    setModalView({ mode: "list" });
    onDismiss();
  };

  const handleStartCreateService = () => {
    setModalView({ mode: "create" });
  };

  const handleCancelCreateService = () => {
    if (isSubmitting) return;

    setModalView({ mode: "list" });
  };

  const handleCancelPhysicalChipDelivery = () => {
    if (processingServiceId !== undefined) return;

    setModalView({ mode: "list" });
  };

  const handleRefreshPaymentLinks = () => {
    paymentLinkPollingAttempts.current = 0;
    setHasPaymentLinkPollingTimedOut(false);

    void queryClient.refetchQueries({
      queryKey: [LINE_ADDITIONAL_SERVICES_QUERY_TOKEN]
    });
  };

  const processStep = async (
    serviceId: number,
    payload: ProcessLineAdditionalServiceStepPayload,
    messages = {
      success: "Etapa confirmada com sucesso.",
      error: "Não foi possível confirmar a etapa do serviço adicional."
    }
  ): Promise<boolean> => {
    setProcessingServiceId(serviceId);

    try {
      const request = await execute<LineAdditionalService>({
        method: "PATCH",
        url: `line-additional-services/${serviceId}/process-step`,
        bodyObj: payload,
        showAlert: true,
        messages
      });

      if (request?.response?.ok === true) {
        await queryClient.invalidateQueries({
          queryKey: [LINE_ADDITIONAL_SERVICES_QUERY_TOKEN]
        });

        return true;
      }

      return false;
    } finally {
      setProcessingServiceId(undefined);
    }
  };

  const handleCheckPaymentStatus = (service: LineAdditionalServiceListItem) => {
    void processStep(
      service.id,
      {
        interactionAction:
          LineAdditionalServiceInteractionAction.CONFIRM_PAYMENT
      },
      {
        success: "Pagamento confirmado com sucesso.",
        error:
          "Não foi possível confirmar o pagamento. Verifique se a cobrança já foi paga."
      }
    );
  };

  const handleProcessStep = (service: LineAdditionalServiceListItem) => {
    if (isLineAdditionalServiceStepTerminal(service)) return;

    const interactionAction = service.nextStep?.interactionAction;

    if (!interactionAction) return;

    const isPhysicalChipDelivery =
      service.type === LineAdditionalServiceType.PHYSICAL_CHIP &&
      interactionAction ===
        LineAdditionalServiceInteractionAction.DELIVER_SERVICE;

    if (isPhysicalChipDelivery) {
      setModalView({
        mode: "confirmPhysicalChipDelivery",
        service
      });

      return;
    }

    void processStep(service.id, { interactionAction });
  };

  const handleCancelService = (service: LineAdditionalServiceListItem) => {
    openModalWithSettings(
      "Cancelar serviço adicional?",
      "Esta ação cancela o serviço e pode cancelar ou reembolsar a cobrança associada, quando aplicável.",
      () => {
        void processStep(
          service.id,
          {
            interactionAction: LineAdditionalServiceInteractionAction.CANCEL
          },
          {
            success: "Serviço adicional cancelado com sucesso.",
            error: "Não foi possível cancelar o serviço adicional."
          }
        );
      },
      {
        primaryButtonText: "Cancelar serviço",
        secondaryButtonText: "Voltar"
      }
    );
  };

  const handlePhysicalChipDeliverySubmit = async (
    values: PhysicalChipDeliveryFormValues
  ) => {
    if (modalView.mode !== "confirmPhysicalChipDelivery") return;

    const rescueProtocol = values.rescueProtocol.trim();

    const payload: ProcessLineAdditionalServiceStepPayload = {
      interactionAction: LineAdditionalServiceInteractionAction.DELIVER_SERVICE,
      iccid: values.iccid.trim(),
      trackingCode: values.deliveryByMail
        ? values.trackingCode.trim()
        : HAND_DELIVERY_TRACKING_CODE,
      rescueProtocol
    };

    const wasProcessed = await processStep(modalView.service.id, payload);

    if (wasProcessed) {
      setModalView({ mode: "list" });
    }
  };

  const handleSubmit = async (values: LineAdditionalServiceFormValues) => {
    if (!item?.lineId) return;

    const payload: CreateLineAdditionalServicePayload = {
      lineId: item.lineId,
      contractId,
      ...values
    };

    const response = await execute<LineAdditionalService>({
      method: "POST",
      url: "line-additional-services",
      bodyObj: payload,
      showAlert: true,
      messages: {
        success: getSuccessMessage(values),
        error: "Não foi possível registrar o serviço adicional da linha."
      },
      setIsFetching: setIsSubmitting
    });

    if (response?.response?.ok === true) {
      await queryClient.invalidateQueries({
        queryKey: [LINE_ADDITIONAL_SERVICES_QUERY_TOKEN]
      });

      setModalView({ mode: "list" });
    }
  };

  const dialogContentProps = {
    type: DialogType.normal,
    title: `Serviços Adicionais da Linha: ${lineNumber}`
  };
  let dialogMinWidth = 760;

  if (isCreatingService) {
    dialogMinWidth = 1040;
  } else if (modalView.mode === "confirmPhysicalChipDelivery") {
    dialogMinWidth = 520;
  }

  return (
    <Dialog
      hidden={!isOpen}
      onDismiss={handleClose}
      dialogContentProps={dialogContentProps}
      minWidth={dialogMinWidth}
      styles={{
        main: {
          selectors: {
            "@media(max-width: 760px)": {
              height: "100vh",
              minHeight: "100vh",
              minWidth: "100vw",
              width: "100vw"
            }
          }
        }
      }}
    >
      <Stack
        tokens={{ childrenGap: 16 }}
        style={{ maxHeight: "80vh", overflowY: "auto", paddingRight: 8 }}
      >
        {modalView.mode === "create" && (
          <LineAdditionalServiceForm
            allowedServiceCategories={allowedServiceCategories}
            isSubmitting={isSubmitting}
            formId={lineAdditionalServiceFormId}
            onSubmit={values => void handleSubmit(values)}
            onCancel={handleCancelCreateService}
          />
        )}
        {modalView.mode === "confirmPhysicalChipDelivery" && (
          <PhysicalChipDeliveryForm
            formId={physicalChipDeliveryFormId}
            isSubmitting={processingServiceId === modalView.service.id}
            onSubmit={values => void handlePhysicalChipDeliverySubmit(values)}
            onCancel={handleCancelPhysicalChipDelivery}
          />
        )}
        {modalView.mode === "list" && (
          <LineAdditionalServicesList
            services={services}
            isLoading={isLoading}
            hasPaymentLinkPollingTimedOut={hasPaymentLinkPollingTimedOut}
            processingServiceId={processingServiceId}
            onCreate={handleStartCreateService}
            onCancel={handleCancelService}
            onCheckPaymentStatus={handleCheckPaymentStatus}
            onProcessStep={handleProcessStep}
            onRefreshPaymentLinks={handleRefreshPaymentLinks}
          />
        )}
      </Stack>

      <DialogFooter>
        <DefaultButton text="Fechar" onClick={handleClose} disabled={isBusy} />
        {isCreatingService && (
          <PrimaryButton
            type="submit"
            form={lineAdditionalServiceFormId}
            text="Registrar serviço"
            disabled={isSubmitting || isLoading}
          />
        )}
        {modalView.mode === "confirmPhysicalChipDelivery" && (
          <PrimaryButton
            type="submit"
            form={physicalChipDeliveryFormId}
            text={
              modalView.service.nextStep?.interactionName ??
              modalView.service.nextStep?.friendlyName ??
              "Confirmar serviço"
            }
            disabled={processingServiceId !== undefined}
          />
        )}
      </DialogFooter>
    </Dialog>
  );
}

export default LineAdditionalServicesModal;
